import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useTransform, animate, useScroll } from 'framer-motion';

interface HeroProps {
  eyebrow?: string;
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaHref: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
  socialProof?: string;
  metrics?: Array<{ value: string; label: string }>;
}

// ============================================
// NEURAL NETWORK CANVAS VISUALIZATION
// ============================================
const NeuralNetworkCanvas: React.FC<{
  mouseX: number;
  mouseY: number;
  scrollY: number;
}> = ({ mouseX, mouseY, scrollY }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<Array<{
    x: number;
    y: number;
    baseX: number;
    baseY: number;
    vx: number;
    vy: number;
    radius: number;
    connections: number[];
  }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize nodes for neural network
    const nodeCount = 40;
    const nodes: typeof nodesRef.current = [];

    for (let i = 0; i < nodeCount; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      nodes.push({
        x,
        y,
        baseX: x,
        baseY: y,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1,
        connections: [],
      });
    }

    // Create connections between nearby nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].baseX - nodes[j].baseX;
        const dy = nodes[i].baseY - nodes[j].baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          nodes[i].connections.push(j);
        }
      }
    }

    nodesRef.current = nodes;

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const nodes = nodesRef.current;
    const normalizedMouseX = (mouseX / 100) * canvas.width;
    const normalizedMouseY = (mouseY / 100) * canvas.height;

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        // Mouse influence
        const dx = normalizedMouseX - node.x;
        const dy = normalizedMouseY - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 300;

        if (dist < maxDist) {
          const force = (1 - dist / maxDist) * 30;
          node.x += (dx / dist) * force * 0.02;
          node.y += (dy / dist) * force * 0.02;
        }

        // Scroll influence - nodes drift up as you scroll
        const scrollOffset = scrollY * 0.1;
        node.y = node.baseY - scrollOffset + Math.sin(Date.now() * 0.001 + i) * 5;

        // Gentle floating motion
        node.x = node.baseX + Math.sin(Date.now() * 0.0005 + i * 0.5) * 10;

        // Draw connections
        for (const connIndex of node.connections) {
          const connNode = nodes[connIndex];
          const connDx = node.x - connNode.x;
          const connDy = node.y - connNode.y;
          const connDist = Math.sqrt(connDx * connDx + connDy * connDy);

          if (connDist < 250) {
            const alpha = (1 - connDist / 250) * 0.15;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(connNode.x, connNode.y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }

        // Draw node with glow
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 4
        );
        gradient.addColorStop(0, 'rgba(139, 92, 246, 0.8)');
        gradient.addColorStop(0.5, 'rgba(99, 102, 241, 0.3)');
        gradient.addColorStop(1, 'rgba(99, 102, 241, 0)');

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 4, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mouseX, mouseY, scrollY]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
};

// ============================================
// WARPING GRID COMPONENT
// ============================================
const WarpingGrid: React.FC<{ mouseX: number; mouseY: number }> = ({ mouseX, mouseY }) => {
  const gridSize = 12;
  const lines = useMemo(() => {
    const horizontal: JSX.Element[] = [];
    const vertical: JSX.Element[] = [];

    for (let i = 0; i <= gridSize; i++) {
      horizontal.push(
        <motion.line
          key={`h-${i}`}
          x1="0"
          y1={`${(i / gridSize) * 100}%`}
          x2="100%"
          y2={`${(i / gridSize) * 100}%`}
          stroke="rgba(99, 102, 241, 0.08)"
          strokeWidth="1"
        />
      );
      vertical.push(
        <motion.line
          key={`v-${i}`}
          x1={`${(i / gridSize) * 100}%`}
          y1="0"
          x2={`${(i / gridSize) * 100}%`}
          y2="100%"
          stroke="rgba(99, 102, 241, 0.08)"
          strokeWidth="1"
        />
      );
    }
    return [...horizontal, ...vertical];
  }, []);

  // Calculate warp intensity based on mouse position
  const warpX = (mouseX - 50) * 0.3;
  const warpY = (mouseY - 50) * 0.3;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{
        transform: `perspective(1000px) rotateX(${warpY * 0.1}deg) rotateY(${warpX * 0.1}deg)`,
        transition: 'transform 0.3s ease-out',
      }}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter="url(#glow)">{lines}</g>
    </svg>
  );
};

// ============================================
// HOLOGRAPHIC SERVICE CARD
// ============================================
const HolographicCard: React.FC<{
  title: string;
  icon: string;
  position: { x: string; y: string };
  delay: number;
}> = ({ title, icon, position, delay }) => {
  return (
    <motion.div
      className="absolute hidden lg:flex"
      style={{ left: position.x, top: position.y }}
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: delay + 1.5, duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
    >
      <motion.div
        className="relative group cursor-pointer"
        animate={{
          y: [0, -8, 0],
        }}
        transition={{
          duration: 4 + delay,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        whileHover={{ scale: 1.05, y: -10 }}
      >
        {/* Holographic glow effect */}
        <div className="absolute -inset-[1px] rounded-xl bg-gradient-to-r from-blue-500/50 via-violet-500/50 to-cyan-500/50 blur-sm opacity-60 group-hover:opacity-100 transition-opacity" />

        {/* Scanline effect */}
        <motion.div
          className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none"
          style={{ zIndex: 10 }}
        >
          <motion.div
            className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent"
            animate={{
              top: ['-10%', '110%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </motion.div>

        {/* Card content */}
        <div className="relative px-4 py-3 rounded-xl bg-zinc-900/90 border border-zinc-700/50 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <span className="text-xl">{icon}</span>
            <span className="text-sm font-medium text-zinc-200 whitespace-nowrap">{title}</span>
          </div>

          {/* Holographic shimmer */}
          <motion.div
            className="absolute inset-0 rounded-xl opacity-30"
            style={{
              background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 55%, transparent 60%)',
              backgroundSize: '200% 100%',
            }}
            animate={{
              backgroundPosition: ['200% 0', '-200% 0'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// NOISE TEXTURE OVERLAY
// ============================================
const NoiseOverlay: React.FC = () => {
  return (
    <motion.div
      className="absolute inset-0 pointer-events-none opacity-[0.015]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      }}
      animate={{
        x: [0, 10, 0],
        y: [0, 10, 0],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );
};

// ============================================
// PARTICLE MESH WITH SCROLL REACTION
// ============================================
const ParticleMeshCanvas: React.FC<{
  scrollY: number;
  mouseX: number;
  mouseY: number;
}> = ({ scrollY, mouseX, mouseY }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Array<{
    x: number;
    y: number;
    z: number;
    baseX: number;
    baseY: number;
    speed: number;
    size: number;
  }>>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles in a grid mesh pattern
    const particles: typeof particlesRef.current = [];
    const cols = 25;
    const rows = 15;

    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const x = (i / (cols - 1)) * canvas.width;
        const y = (j / (rows - 1)) * canvas.height;
        particles.push({
          x,
          y,
          z: Math.random(),
          baseX: x,
          baseY: y,
          speed: 0.5 + Math.random() * 0.5,
          size: 1 + Math.random() * 1.5,
        });
      }
    }

    particlesRef.current = particles;

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particles = particlesRef.current;
    const cols = 25;

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const normalizedMouseX = (mouseX / 100) * canvas.width;
      const normalizedMouseY = (mouseY / 100) * canvas.height;
      const time = Date.now() * 0.001;

      // Update particles and create mesh
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Wave effect based on scroll
        const scrollWave = Math.sin(p.baseX * 0.005 + scrollY * 0.005) * 20;
        const scrollWave2 = Math.cos(p.baseY * 0.003 + scrollY * 0.003) * 15;

        // Mouse influence
        const dx = normalizedMouseX - p.baseX;
        const dy = normalizedMouseY - p.baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = 200;
        let mouseInfluenceX = 0;
        let mouseInfluenceY = 0;

        if (dist < maxDist) {
          const force = (1 - dist / maxDist) * 40;
          mouseInfluenceX = (dx / dist) * force;
          mouseInfluenceY = (dy / dist) * force;
        }

        // Gentle floating
        const floatX = Math.sin(time * p.speed + i * 0.1) * 5;
        const floatY = Math.cos(time * p.speed * 0.8 + i * 0.1) * 5;

        p.x = p.baseX + scrollWave + mouseInfluenceX + floatX;
        p.y = p.baseY + scrollWave2 + mouseInfluenceY + floatY;

        // Draw mesh lines to adjacent particles
        const col = i % cols;
        const row = Math.floor(i / cols);

        // Connect to right neighbor
        if (col < cols - 1) {
          const rightNeighbor = particles[i + 1];
          const alpha = 0.05 + p.z * 0.05;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(rightNeighbor.x, rightNeighbor.y);
          ctx.strokeStyle = `rgba(99, 102, 241, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }

        // Connect to bottom neighbor
        if (row < Math.floor(particles.length / cols) - 1) {
          const bottomNeighbor = particles[i + cols];
          const alpha = 0.05 + p.z * 0.05;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(bottomNeighbor.x, bottomNeighbor.y);
          ctx.strokeStyle = `rgba(139, 92, 246, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (0.5 + p.z * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.1 + p.z * 0.2})`;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mouseX, mouseY, scrollY]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.4 }}
    />
  );
};

// ============================================
// EXISTING COMPONENTS (kept from Prompt 1)
// ============================================

const Particle: React.FC<{
  index: number;
  mouseX: number;
  mouseY: number;
}> = ({ index, mouseX, mouseY }) => {
  const size = useMemo(() => Math.random() * 4 + 1, []);
  const initialX = useMemo(() => Math.random() * 100, []);
  const initialY = useMemo(() => Math.random() * 100, []);
  const depth = useMemo(() => Math.random() * 0.5 + 0.5, []);
  const duration = useMemo(() => Math.random() * 20 + 15, []);
  const delay = useMemo(() => Math.random() * -20, []);

  const offsetX = (mouseX - 50) * depth * 0.3;
  const offsetY = (mouseY - 50) * depth * 0.3;

  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        left: `${initialX}%`,
        top: `${initialY}%`,
        background: `rgba(${140 + Math.random() * 60}, ${160 + Math.random() * 80}, 255, ${0.3 + depth * 0.4})`,
        boxShadow: `0 0 ${size * 2}px rgba(140, 180, 255, ${0.2 + depth * 0.2})`,
        transform: `translate(${offsetX}px, ${offsetY}px)`,
        transition: 'transform 0.3s ease-out',
      }}
      animate={{
        y: [0, -30 * depth, 0],
        x: [0, 15 * (index % 2 === 0 ? 1 : -1), 0],
        opacity: [0.3 + depth * 0.3, 0.6 + depth * 0.3, 0.3 + depth * 0.3],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
};

const OrbitingIcon: React.FC<{
  icon: string;
  radius: number;
  duration: number;
  delay: number;
  reverse?: boolean;
}> = ({ icon, radius, duration, delay, reverse = false }) => {
  return (
    <motion.div
      className="absolute left-1/2 top-1/2"
      style={{ width: 0, height: 0 }}
      animate={{ rotate: reverse ? -360 : 360 }}
      transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
    >
      <motion.div
        className="absolute flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/80 border border-zinc-700/50 backdrop-blur-sm text-lg"
        style={{ left: -20, top: -radius }}
        animate={{ rotate: reverse ? 360 : -360 }}
        transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
        whileHover={{ scale: 1.2 }}
      >
        {icon}
      </motion.div>
    </motion.div>
  );
};

const CountingNumber: React.FC<{ value: string; delay?: number }> = ({ value, delay = 0 }) => {
  const [displayValue, setDisplayValue] = useState("0");
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    const numericMatch = value.match(/^[\d.]+/);
    const suffix = value.replace(/^[\d.]+/, '');

    if (numericMatch) {
      const targetNum = parseFloat(numericMatch[0]);
      const isDecimal = numericMatch[0].includes('.');

      const timeout = setTimeout(() => {
        animate(0, targetNum, {
          duration: 2,
          ease: [0.32, 0.72, 0, 1],
          onUpdate: (latest) => {
            if (isDecimal) {
              setDisplayValue(latest.toFixed(1) + suffix);
            } else {
              setDisplayValue(Math.round(latest) + suffix);
            }
          },
        });
      }, delay * 1000);

      return () => clearTimeout(timeout);
    } else {
      setDisplayValue(value);
    }
  }, [value, delay]);

  return <>{displayValue}</>;
};

const MagneticButton: React.FC<{
  href: string;
  children: React.ReactNode;
  className?: string;
}> = ({ href, children, className }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 15, stiffness: 150 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * 0.3);
    y.set((e.clientY - centerY) * 0.3);
  }, [x, y]);

  const handleMouseLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return (
    <motion.a
      ref={ref}
      href={href}
      className={className}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.a>
  );
};

const ShimmerText: React.FC<{ children: string; className?: string }> = ({ children, className }) => {
  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative">
        {children}
        <motion.span
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
          }}
          animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
      </span>
    </span>
  );
};

const LetterReveal: React.FC<{ text: string; className?: string; delay?: number }> = ({
  text,
  className,
  delay = 0
}) => {
  const words = text.split(' ');

  return (
    <span className={className}>
      {words.map((word, wordIndex) => (
        <span key={wordIndex} className="inline-block">
          {word.split('').map((letter, letterIndex) => {
            const totalIndex = words.slice(0, wordIndex).join(' ').length + letterIndex + wordIndex;
            const isPartOfNumber = /[\d$%+MKB.-]/.test(letter);

            return (
              <motion.span
                key={letterIndex}
                className={`inline-block ${isPartOfNumber ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400' : ''}`}
                initial={{ opacity: 0, y: 20, rotateX: -90 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{
                  duration: 0.4,
                  delay: delay + totalIndex * 0.02,
                  ease: [0.215, 0.61, 0.355, 1],
                }}
              >
                {letter}
              </motion.span>
            );
          })}
          {wordIndex < words.length - 1 && <span>&nbsp;</span>}
        </span>
      ))}
    </span>
  );
};

// ============================================
// MAIN HERO COMPONENT
// ============================================
export const Hero: React.FC<HeroProps> = ({
  eyebrow,
  headline,
  subheadline,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
  metrics,
}) => {
  const containerRef = useRef<HTMLElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = useState(false);

  // Scroll tracking
  const { scrollY } = useScroll();
  const [scrollYValue, setScrollYValue] = useState(0);

  useEffect(() => {
    return scrollY.on('change', (latest) => {
      setScrollYValue(latest);
    });
  }, [scrollY]);

  // Mouse tracking
  const mouseX = useMotionValue(50);
  const mouseY = useMotionValue(50);
  const smoothMouseX = useSpring(mouseX, { damping: 50, stiffness: 300 });
  const smoothMouseY = useSpring(mouseY, { damping: 50, stiffness: 300 });

  const rotateX = useTransform(smoothMouseY, [0, 100], [5, -5]);
  const rotateY = useTransform(smoothMouseX, [0, 100], [-5, 5]);
  const spotlightX = useTransform(smoothMouseX, [0, 100], ['0%', '100%']);
  const spotlightY = useTransform(smoothMouseY, [0, 100], ['0%', '100%']);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      mouseX.set(x);
      mouseY.set(y);
      setMousePosition({ x, y });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, [mouseX, mouseY]);

  const particles = useMemo(() => Array.from({ length: 40 }, (_, i) => i), []);
  const techIcons = ['⚡', '🤖', '📊', '🚀', '💡', '🔮'];

  // Service cards for holographic previews
  const serviceCards = [
    { title: 'AI Audit Partnership', icon: '🤝', position: { x: '8%', y: '25%' }, delay: 0 },
    { title: 'AI Implementation', icon: '⚡', position: { x: '85%', y: '30%' }, delay: 0.3 },
    { title: 'VibeCode', icon: '🚀', position: { x: '5%', y: '65%' }, delay: 0.6 },
  ];

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center bg-[#0a0a0a] overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Noise texture overlay */}
      <NoiseOverlay />

      {/* WebGL Particle Mesh with scroll reaction */}
      <ParticleMeshCanvas
        scrollY={scrollYValue}
        mouseX={mousePosition.x}
        mouseY={mousePosition.y}
      />

      {/* Neural Network visualization */}
      <NeuralNetworkCanvas
        mouseX={mousePosition.x}
        mouseY={mousePosition.y}
        scrollY={scrollYValue}
      />

      {/* Dynamic warping grid */}
      <WarpingGrid mouseX={mousePosition.x} mouseY={mousePosition.y} />

      {/* Mouse-following spotlight */}
      <motion.div
        className="absolute w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)',
          left: spotlightX,
          top: spotlightY,
          x: '-50%',
          y: '-50%',
        }}
      />

      {/* CSS Particles (reduced count, mesh handles more) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((i) => (
          <Particle key={i} index={i} mouseX={mousePosition.x} mouseY={mousePosition.y} />
        ))}
      </div>

      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-1/4 -left-1/4 w-[300px] sm:w-[400px] md:w-[500px] lg:w-[600px] h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] bg-blue-500/10 rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -top-1/4 -right-1/4 w-[250px] sm:w-[350px] md:w-[450px] lg:w-[500px] h-[250px] sm:h-[350px] md:h-[450px] lg:h-[500px] bg-violet-500/10 rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4],
            x: [0, -30, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[100px]"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Floating holographic service cards */}
      {serviceCards.map((card, i) => (
        <HolographicCard
          key={i}
          title={card.title}
          icon={card.icon}
          position={card.position}
          delay={card.delay}
        />
      ))}

      {/* Orbiting tech icons */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {techIcons.map((icon, i) => (
            <OrbitingIcon
              key={i}
              icon={icon}
              radius={280 + (i % 2) * 80}
              duration={20 + i * 5}
              delay={i * 2}
              reverse={i % 2 === 0}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32 text-center z-10" style={{ perspective: 1000 }}>
        {/* Badge */}
        {eyebrow && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-blue-400"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [1, 0.5, 1],
                boxShadow: [
                  '0 0 0 0 rgba(96, 165, 250, 0.7)',
                  '0 0 0 8px rgba(96, 165, 250, 0)',
                  '0 0 0 0 rgba(96, 165, 250, 0)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">{eyebrow}</span>
          </motion.div>
        )}

        {/* Headline with 3D tilt */}
        <motion.div
          style={{
            rotateX: isHovering ? rotateX : 0,
            rotateY: isHovering ? rotateY : 0,
            transformStyle: 'preserve-3d',
          }}
          transition={{ type: 'spring', damping: 30, stiffness: 200 }}
        >
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-8"
          >
            <LetterReveal text={headline} delay={0.3} />
          </motion.h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
          className="text-lg sm:text-xl text-zinc-400 leading-relaxed mb-12 max-w-3xl mx-auto"
        >
          <ShimmerText>{subheadline}</ShimmerText>
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <MagneticButton
            href={ctaHref}
            className="group relative inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-white text-zinc-900 font-semibold rounded-xl overflow-hidden text-sm sm:text-base"
          >
            <motion.div
              className="absolute -inset-[2px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6)',
                backgroundSize: '300% 100%',
              }}
              animate={{ backgroundPosition: ['0% 0%', '300% 0%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
            <span className="relative z-10 bg-white px-6 sm:px-8 py-3 sm:py-4 rounded-[10px] flex items-center gap-2 sm:gap-3 -m-[2px] group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-violet-500 group-hover:text-white transition-all duration-300">
              {ctaText}
              <motion.svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </motion.svg>
            </span>
          </MagneticButton>

          {secondaryCtaText && secondaryCtaHref && (
            <motion.a
              href={secondaryCtaHref}
              className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-zinc-300 font-medium rounded-xl border border-zinc-800 hover:border-zinc-600 hover:bg-zinc-800/30 backdrop-blur-sm transition-all duration-300 text-sm sm:text-base"
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {secondaryCtaText}
              <svg
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.a>
          )}
        </motion.div>

        {/* Metrics */}
        {metrics && metrics.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
            className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-12 lg:gap-16 pt-8 sm:pt-12 border-t border-zinc-800/50"
          >
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.6,
                  delay: 1.4 + index * 0.15,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="text-center group cursor-default"
              >
                <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">
                  <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent group-hover:from-blue-400 group-hover:via-violet-400 group-hover:to-blue-400 transition-all duration-500">
                    <CountingNumber value={metric.value} delay={1.5 + index * 0.2} />
                  </span>
                </p>
                <p className="text-zinc-500 text-sm uppercase tracking-wider group-hover:text-zinc-400 transition-colors">
                  {metric.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />

      {/* Breathing scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer z-20"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <motion.span
          className="text-zinc-500 text-xs uppercase tracking-widest"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Scroll
        </motion.span>
        <motion.div
          className="w-6 h-10 rounded-full border-2 border-zinc-700 flex justify-center pt-2"
          animate={{ borderColor: ['rgb(63, 63, 70)', 'rgb(113, 113, 122)', 'rgb(63, 63, 70)'] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-zinc-500"
            animate={{ y: [0, 16, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
